import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../../common/components/form/InputFormik";
import { useState } from "react";
import { maskChassi, maskPhone } from "../../../../common/helpers/mask.helpers";
import { IMaintenance } from "../../../../types/maintenance.type";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import { isLicensePlate } from "../../../../common/helpers/is-license-plate";

const validationSchema = Yup.object().shape({
  licensePlate: Yup.string()
    .required("placa é obrigatório")
    .test("invalid-plate", "placa inválida", isLicensePlate),
  model: Yup.string().required("O modelo do veículo é obrigatório"),
  chassi: Yup.string().required("O número do chassi é obrigatório"),
  typeOfEvent: Yup.string().required("O tipo de evento é obrigatório"),
  odometer: Yup.string().required("Hodômetro é obrigatória"),
  typeMaintenance: Yup.string()
    .oneOf(["Interna", "Externa"], "Selecione um tipo válido")
    .required("Tipo de manutenção é obrigatório"),
  collision: Yup.boolean().default(false),
  registers: Yup.array().of(
    Yup.object().shape({
      file: Yup.mixed().default(undefined),
      type: Yup.string().default(""),
      content: Yup.string().default(""),
      verified: Yup.boolean().default(false),
    })
  ),
});

type FormPropsCreateMaintenance = {
  onSubmit: (
    values: IMaintenance
  ) => Promise<{ ok: boolean; data: IMaintenance; errors?: unknown[] }>;
  data: IMaintenance;
  formRequestErrors?: { message: string; path: string[] }[];
};

function FormEditMaintenance(props: FormPropsCreateMaintenance) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [theInitialData, setNewInitialData] = useState<IMaintenance>(
    Object.assign(props.data, {})
  );
  const renterId = props.data.renterId;
  const vehicleId = props.data.vehicleId;
  const formik = useFormik({
    initialValues: theInitialData,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (data: IMaintenance) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      const dataWithRenterId = {
        ...data,
        renterId,
        vehicleId,
        odometer: data.odometer.split(",").join("."),
      };
      const result = await props.onSubmit(dataWithRenterId);
      setIsSubmitting(false);
      
      if (result.ok) {
        const next: IMaintenance = {
          ...result.data,
          collision: Boolean(result.data.collision),
        };

        formik.setValues(next);
        setNewInitialData(next);
      }
      return result;
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold mb-4">
            Dados do Veículo e do Cliente
          </h2>

          <div className="grid grid-cols-3 gap-2 mb-4">
            {/* Primeira linha */}
            <div className="grid grid-cols-1 col-span-3 gap-2 sm:grid-cols-2 xl:grid-cols-3 ">
              <InputFormik
                label="Placa do veículo"
                name="licensePlate"
                required={true}
                readonly={true}
              />

              <InputFormik
                label="Titular do veículo"
                name="partnerCompanyName"
                required={true}
                readonly={true}
              />

              <InputFormik
                label="Chassi"
                name="chassi"
                mask={maskChassi}
                required={true}
                readonly={true}
              />

              <InputFormik
                label="Modelo"
                name="model"
                required={true}
                readonly={true}
              />

              <InputFormik label="Hodômetro:" name="odometer" required={true} />

              <InputFormik
                label="Nome Completo"
                name="name"
                required={true}
                readonly={true}
              />

              <InputFormik
                label="WhatsApp"
                name="whatsapp"
                type="whatsapp"
                required={true}
                mask={maskPhone}
                readonly={true}
              />

              <InputFormik
                label="CPF do cliente"
                name="cpf"
                required={true}
                readonly={true}
              />

              <ComboBoxFormik
                name="typeOfEvent"
                label="Tipo de Evento"
                options={[
                  { value: "-", label: "-" },
                  {
                    value: "Manutenção Programada",
                    label: "Manutenção Programada",
                  },
                  {
                    value: "Manutenção Não Programada",
                    label: "Manutenção Não Programada",
                  },
                ]}
              />
              <ComboBoxFormik
                name="typeMaintenance"
                label="Tipo de Manutenção"
                options={[
                  { value: "", label: "Selecione" },
                  { value: "Interna", label: "Manutenção Interna" },
                  { value: "Externa", label: "Manutenção Externa" },
                ]}
              />

              <div className="flex items-center gap-2 mt-2">
                <input
                  id="collision"
                  type="checkbox"
                  name="collision"
                  checked={!!formik.values.collision}
                  onChange={(e) => formik.setFieldValue("collision", e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="collision" className="text-sm">
                  Colisão
                </label>
              </div>
            </div>
          </div>
        </section>
        <div className="w-full flex justify-end">
          <ButtonWithLoader
            isLoading={isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}
          >
            Salvar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}

export default FormEditMaintenance;
